import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";

import { SurveyCreatorComponent, SurveyCreator } from "survey-creator-react";
import "survey-core/defaultV2.css";
import "survey-creator-core/survey-creator-core.css";

import useGetAdvancedSurveyById from "../../hooks/useGetAdvancedSurveyById";
import useCreateAdvancedSurveyMutation from "../../hooks/useCreateAdvancedSurveyMutation";
import useUpdateAdvancedSurveyMutation from "../../hooks/useUpdateAdvancedSurveyMutation";
import NavBar from "../../components/navBar/navBar";
import { showApiErrorToast } from "../../utils/helpers";
import Loader from "../../components/Loader";

const AdvancedSurveysCreator = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const surveyId = location.state?.surveyId;

  const [creator, setCreator] = useState(null);
  const [name, setName] = useState("");

  const { data, isLoading, isError, error } = useGetAdvancedSurveyById({
    surveyId,
    enabled: Boolean(surveyId),
  });

  useEffect(() => {
    if (isError) {
      showApiErrorToast(error, "Failed to load survey");
    }
  }, [isError, error]);

  const createMutation = useCreateAdvancedSurveyMutation();
  const updateMutation = useUpdateAdvancedSurveyMutation();

  // Initialize Survey Creator (ONCE)
  useEffect(() => {
    if (!creator) {
      const options = {
        showLogicTab: true,
        showTranslationTab: true,
        isAutoSave: false,
      };

      const creatorInstance = new SurveyCreator(options);
      creatorInstance.showState = true;

      setCreator(creatorInstance);
    }
  }, [creator]);

  // Load survey data (EDIT MODE)
  useEffect(() => {
    if (creator && data) {
      setName(data.name || "");
      creator.JSON = data.survey_json || {};
    }
  }, [creator, data]);

  const handleSave = () => {
    if (!name?.trim()) {
      toast.error("ePRO name is required");
      return;
    }

    if (!creator) {
      toast.error("ePRO editor not ready");
      return;
    }

    const payload = {
      name,
      survey_json: creator.JSON,
    };

    if (surveyId) {
      updateMutation.mutate({ surveyId, payload });
    } else {
      createMutation.mutate(payload, {
        onSuccess: (data) => {
          navigate("/advanced-surveys-creator", {
            state: { surveyId: data.id },
            replace: true,
          });
        },
      });
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: "95%", mx: "auto" }}>
        <NavBar active="advanced_surveys" />

        <Stack spacing={2} my={2}>
          <Typography variant="h5" fontWeight={600}>
            {surveyId ? "Edit ePRO" : "Create ePRO"}
          </Typography>

          <TextField
            label="ePRO Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ width: "30%" }}
            size="small"
            disabled={isLoading}
            slotProps={{ htmlInput: { "data-testid": "epro-name-input" } }}
          />

          <Stack direction="row" spacing={2}>
            <Button data-testid="epro-save-btn" variant="contained" onClick={handleSave}>
              Save
            </Button>

            <Button
              data-testid="epro-cancel-btn"
              variant="outlined"
              onClick={() => navigate("/advanced-surveys")}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* SurveyJS Creator */}

      {surveyId && isLoading ? (
        <Loader />
      ) : (
        creator && (
          <Box
            sx={{
              height: "calc(100vh - 220px)",
            }}
          >
            <SurveyCreatorComponent creator={creator} />
          </Box>
        )
      )}
    </>
  );
};

export default AdvancedSurveysCreator;
